import * as cdr from "./SicCodesDialog.reducer";
import SicCodesTransformer from "./SicCodesTransformer";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slide,
  Snackbar,
} from "@mui/material";
import FilesFileUpload from "components/FilesFileUpload";
import Disabled from "components/common/Disabled";
import CentredLoader from "components/inputs/ingestion/CentredLoader";
import ColumnMapper from "components/inputs/ingestion/ColumnMapper";
import SheetSelector from "components/inputs/ingestion/SheetSelector";
import { tabularMimeTypes } from "fileUtils";
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import * as tablesService from "services/tablesService";

const SicCodesUpload = ({ uploadStarted, uploadCompleted, aborter }) => {
  const onFileDrop = (id, file) => {
    uploadCompleted(id, file);
  };

  return (
    <Disabled ifReadOnly>
      <FilesFileUpload
        onDrop={onFileDrop}
        asAtDateNotRequired
        size={{ md: 4 }}
        waitText={false}
        mimeTypes={tabularMimeTypes}
        uploadInitiated={uploadStarted}
        aborter={aborter}
      />
    </Disabled>
  );
};

const SicCodesDialog = ({
  startFileProcessingRef = null,
  noDropBox = false,
}) => {
  const [mappingConfig, setMappingConfig] = useState(cdr.clearMapping());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [tablesList, setTablesList] = useState(null);
  const [isConversionComplete, setIsConversionComplete] = useState(false);
  const [targetColumnList, setTargetColumnList] = useState([]);
  const [targetColumnMap, setTargetColumnMap] = useState({});
  const [sourceHeaders, setSourceHeaders] = useState([]);
  const [requiredColumns, setRequiredColumns] = useState([]);
  const [outputColums, setOutputColums] = useState([]);
  const dialogContentRef = useRef();

  const {
    data: mappingConfiguration,
    status: mappingConfigurationStatus,
  } = tablesService.useGetColumnsQuery("sic_codes");

  useImperativeHandle(
    startFileProcessingRef,
    () => ({
      start: (fileId, filename) => {
        setMappingConfig((cd) => cdr.setFileId(cd, { fileId }));
        setMappingConfig((cd) => cdr.setFilename(cd, { filename }));
        setStep(1);
        setDialogOpen(true);
      },
    }),
    []
  );

  useEffect(() => {
    if (mappingConfigurationStatus === "fulfilled") {
      const columns = mappingConfiguration.columns;
      setOutputColums(columns);

      const reqCols = columns
        .filter((e) => e.required)
        .map((col) => col.column);
      setRequiredColumns(reqCols);
    }
  }, [mappingConfigurationStatus, mappingConfiguration]);
  const reset = useCallback(() => {
    abortCommand();
    setStep(0);
    setDialogOpen(false);
    setMappingConfig(cdr.clearMapping());
    setTablesList([]);
    setTargetColumnMap({});
    setTargetColumnList([]);
    setSourceHeaders([]);
    setIsConversionComplete(false);
  }, []);

  const onPrev = () => setStep(Math.max(step - 1, 1));
  const onNext = () => setStep(Math.min(step + 1, steps.length - 1));

  const {
    data: tableSummaryData,
    status: tableSummaryStatus,
  } = tablesService.useGetTableSummaryQuery(
    {
      fileId: mappingConfig?.fileId,
      skipColumns: mappingConfig?.origin?.column ?? 0,
      skipRows: mappingConfig?.origin?.row ?? 0,
      tableName: mappingConfig?.tableName,
      headerRows: mappingConfig?.headerRows,
    },
    { skip: step <= 1 }
  );

  const tableSummary = useMemo(
    () => (tableSummaryStatus === "fulfilled" && tableSummaryData) || [],
    [tableSummaryData, tableSummaryStatus]
  );

  const steps = useMemo(
    () => [
      {
        component: (
          <CentredLoader label={`Uploading ${mappingConfig?.filename}`} />
        ),
      },
      {
        title: "Choose Data",
        component: (
          <SheetSelector
            fileId={mappingConfig?.fileId}
            tableName={mappingConfig?.tableName}
            origin={mappingConfig?.origin}
            setTableName={(tableName) =>
              setMappingConfig((cd) => cdr.setTableName(cd, { tableName }))
            }
            setOrigin={(origin) =>
              setMappingConfig((cd) => cdr.setOrigin(cd, { origin }))
            }
            setTablesList={setTablesList}
          />
        ),
        nextDisabled: () => !mappingConfig?.tableName,
      },
      {
        title: "Choose Columns",
        component: (
          <ColumnMapper
            fileId={mappingConfig?.fileId}
            origin={mappingConfig?.origin}
            tableName={mappingConfig?.tableName}
            headerRows={mappingConfig?.headerRows}
            updateColumns={(mappingList) => {
              mappingList.forEach((columnMap) =>
                setMappingConfig((cd) =>
                  cdr.updateColumnMapping(cd, { ...columnMap })
                )
              );
            }}
            targetColumnList={targetColumnList}
            setTargetColumnList={setTargetColumnList}
            targetColumnMap={targetColumnMap}
            setTargetColumnMap={setTargetColumnMap}
            sourceHeaders={sourceHeaders}
            setSourceHeaders={setSourceHeaders}
            outputColumns={outputColums}
            tableSummary={tableSummary}
            parentRef={dialogContentRef}
          />
        ),
        nextDisabled: () =>
          !targetColumnList?.length ||
          !sourceHeaders?.length ||
          !mappingConfig?.mapping?.length ||
          !requiredColumns.every((col) =>
            mappingConfig?.mapping?.some(
              (mapping) => mapping.outputName === col
            )
          ),
      },
      {
        title: "Consistency Checks",
        component: (
          <SicCodesTransformer
            fileId={mappingConfig?.fileId}
            origin={mappingConfig?.origin}
            tableName={mappingConfig?.tableName}
            headerRows={mappingConfig?.headerRows}
            mapping={mappingConfig?.mapping}
            substitutions={mappingConfig?.substitutions}
            rowDeletions={mappingConfig?.rowDeletions}
            claimsKey={mappingConfig?.claimsKey}
            filename={mappingConfig?.filename}
            tablesList={tablesList}
            onConversionComplete={() => {
              reset();
              setIsConversionComplete(true);
            }}
            dateConversionTypeList={mappingConfig?.dateConversionTypes}
            setDateConversionType={(column, type) => {
              setMappingConfig((cd) =>
                cdr.updateDateConversionType(cd, { column, type })
              );
            }}
            onSubstitutionsChanged={(substitutions) => {
              setMappingConfig((cd) =>
                cdr.setSubstitutions(cd, { substitutions })
              );
            }}
            onRowDeletionsChanged={(rowDeletions) => {
              setMappingConfig((cd) =>
                cdr.setRowDeletions(cd, { rowDeletions })
              );
            }}
          />
        ),
      },
    ],
    [
      mappingConfig,
      targetColumnList,
      targetColumnMap,
      sourceHeaders,
      tablesList,
      reset,
      outputColums,
      requiredColumns,
      tableSummary,
    ]
  );

  const nextDisabled =
    step >= steps.length - 1
      ? true
      : typeof steps[step].nextDisabled === "function"
      ? steps[step].nextDisabled()
      : false;

  const activeStep = steps[step];
  const aborter = useRef(null);
  const abortCommand = () => {
    if (aborter.current) aborter.current.abort();
  };
  return (
    <>
      {!noDropBox && (
        <SicCodesUpload
          uploadCompleted={(fileId, _) => {
            setMappingConfig((cd) => cdr.setFileId(cd, { fileId }));
            setStep(1);
          }}
          uploadStarted={(file) => {
            setMappingConfig((cd) =>
              cdr.setFilename(cd, { filename: file.name })
            );
            setDialogOpen(true);
            setStep(0);
          }}
          aborter={aborter}
        />
      )}

      {isConversionComplete && (
        <Snackbar
          open={isConversionComplete}
          autoHideDuration={3500}
          TransitionComponent={Slide}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          onClose={() => setIsConversionComplete(false)}
        >
          <Alert severity={"success"} variant={"filled"}>
            {"SIC codes data imported"}
          </Alert>
        </Snackbar>
      )}

      {dialogOpen && (
        <Dialog
          open
          fullWidth
          maxWidth={false}
          PaperProps={{ sx: { minHeight: "90vh" } }}
        >
          <DialogTitle>{activeStep.title}</DialogTitle>
          <DialogContent ref={dialogContentRef}>
            {activeStep.component}
          </DialogContent>
          <DialogActions sx={{ minHeight: "3.625rem" }}>
            {
              <>
                <Button
                  onClick={reset}
                  disableElevation
                  variant={"contained"}
                  color={"secondary"}
                >
                  {"Cancel"}
                </Button>
                <Box sx={{ flexGrow: 1 }} />
                {step > 0 && (
                  <>
                    <Button
                      onClick={onPrev}
                      disableElevation
                      variant={"contained"}
                      color={"primary"}
                      disabled={step < 2}
                    >
                      {"Prev"}
                    </Button>
                    <Button
                      onClick={onNext}
                      disableElevation
                      variant={"contained"}
                      color={"primary"}
                      disabled={nextDisabled}
                    >
                      {"Next"}
                    </Button>
                  </>
                )}
              </>
            }
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default SicCodesDialog;
